import { memo } from "react";
import { styled } from "styled-components";

import { Image, SourceLabel } from "common/components";
import { breakWordStyle, lineTruncationStyle } from "common/styles";
import { StyledContent } from "domains/knowledgeFeed/components/shared";
import { ExpandableKnowledgeItemWrapper } from "domains/publication/components/ExpandableKnowledgeItemWrapper";
import { KnowledgeDetailButton } from "domains/publication/components/KnowledgeDetailButton";
import type { Signal } from "domains/signals/types";
import type { Source } from "entities/knowledge/types";

const StyledTitle = styled.div`
  font-weight: 500;
  font-size: 1rem;
  margin: 0;
  margin-top: 5px;

  ${breakWordStyle}
  ${lineTruncationStyle(2)}
`;

const StyledRow = styled.div`
  display: flex;
  margin-right: 1rem;
`;

const StyledOuterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledImage = styled(Image)`
  width: 64px;
  min-width: 64px;
  height: 64px;
  border-radius: 4px;
  margin-right: 1rem;
  object-fit: contain;
`;

const StyledKnowledgeDetailButton = styled(KnowledgeDetailButton)`
  flex: 0;
`;

type ExpandableKnowledgeItemProps = {
  id: string;
  title: string;
  summary?: string;
  source: Source;
  timestamp?: number;
  signals: Signal[];
  isBookmarked: boolean;
  isPinned: boolean;
  detailLink: string;
  externalLink?: string;
  isExpandable?: boolean;
};

export const ExpandableKnowledgeItem = memo(
  ({
    id,
    title,
    summary,
    source,
    timestamp,
    signals,
    isBookmarked,
    isPinned,
    detailLink,
    externalLink,
    isExpandable = true,
  }: ExpandableKnowledgeItemProps) => (
    <ExpandableKnowledgeItemWrapper
      id={id}
      title={title}
      summary={summary}
      source={source}
      signals={signals}
      isBookmarked={isBookmarked}
      isPinned={isPinned}
      link={externalLink}
      shareLink={detailLink}
      isExpandable={isExpandable}
      variant="knowledge"
    >
      <StyledOuterRow title={title}>
        <StyledRow>
          {source.images?.thumbnail && <StyledImage src={source.images.thumbnail} alt={source.name} />}
          <StyledContent>
            <StyledTitle>{title}</StyledTitle>
            <SourceLabel date={timestamp} hideImage type="small" source={source} />
          </StyledContent>
        </StyledRow>
        <StyledKnowledgeDetailButton link={detailLink} title={title} />
      </StyledOuterRow>
    </ExpandableKnowledgeItemWrapper>
  ),
);

ExpandableKnowledgeItem.displayName = "ExpandableKnowledgeItem";
